import axiosInstance from 'api/axiosConfig'
import { ENDPOINTS } from 'api/endpoints'
import { ApplicationPipelineStatus } from 'types/workflowStatus'

export type LeaseApplicationPayload = {
  propertyId?: string | number | null
  source?: string
  moveInDate: string
  incomeRange: string
  occupants: number
  hasPets: 'yes' | 'no'
  acceptCreditCheck: boolean
  message?: string
}

export type LeaseApplicationResponse = {
  success: boolean
  message?: string
  data?: {
    id: number
    applicationStatus?: ApplicationPipelineStatus
    requiresVerification?: boolean
  }
}

export type LeaseApplicationStatusResponse = {
  success: boolean
  message?: string
  data?: {
    hasApplied: boolean
    application: {
      id: string | number
      property_fk: string | number
      application_status?: ApplicationPipelineStatus
      updated_at?: string | null
      created_at: string
    } | null
  }
}

export type LeaseApplicationListItem = {
  id: string | number
  property_fk: string | number | null
  property_title?: string | null
  source: string
  move_in_date: string
  income_range: string
  occupants: number
  has_pets: 'yes' | 'no'
  accept_credit_check: 0 | 1
  message?: string | null
  application_status?: ApplicationPipelineStatus
  updated_at?: string | null
  created_at: string
}

function messageFromAxiosError(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: string }).message === 'string') {
    return (err as { message: string }).message
  }
  return 'Échec de l’envoi de la candidature.'
}

export async function submitLeaseApplication(
  payload: LeaseApplicationPayload
): Promise<LeaseApplicationResponse> {
  try {
    const res = await axiosInstance.post<LeaseApplicationResponse>(
      ENDPOINTS.PROPERTY.SUBMIT_APPLICATION,
      payload
    )
    const data = res.data
    if (!data?.success) {
      throw new Error(data?.message || messageFromAxiosError(data))
    }
    return data
  } catch (err: unknown) {
    throw new Error(messageFromAxiosError(err))
  }
}

export async function loadApplicationStatus(
  propertyId: string
): Promise<LeaseApplicationStatusResponse['data']> {
  try {
    const res = await axiosInstance.get<LeaseApplicationStatusResponse>(
      ENDPOINTS.PROPERTY.SUBMIT_APPLICATION,
      { params: { propertyId } }
    )
    if (!res.data?.success) {
      throw new Error(res.data?.message || 'Failed to load application status')
    }
    return res.data.data ?? { hasApplied: false, application: null }
  } catch (err: unknown) {
    throw new Error(messageFromAxiosError(err))
  }
}

export async function loadMyApplications(): Promise<LeaseApplicationListItem[]> {
  try {
    const res = await axiosInstance.get<{
      success: boolean
      message?: string
      data?: LeaseApplicationListItem[]
    }>(ENDPOINTS.PROPERTY.SUBMIT_APPLICATION)
    if (!res.data?.success) {
      throw new Error(res.data?.message || 'Failed to load applications')
    }
    return Array.isArray(res.data.data) ? res.data.data : []
  } catch (err: unknown) {
    throw new Error(messageFromAxiosError(err))
  }
}

export async function withdrawLeaseApplication(
  applicationId: string | number
): Promise<{ success: boolean; message?: string }> {
  try {
    const res = await axiosInstance.delete<{
      success: boolean
      message?: string
    }>(ENDPOINTS.PROPERTY.SUBMIT_APPLICATION, {
      data: { applicationId },
    })
    if (!res.data?.success) {
      throw new Error(res.data?.message || 'Failed to withdraw application')
    }
    return res.data
  } catch (err: unknown) {
    throw new Error(messageFromAxiosError(err))
  }
}
